/*
  pn-cart-discount — the discount code field for the cart drawer and the cart page.

  CONTRACT, in one line: POST /cart/update.js { discount, sections, sections_url }, swap
  the same regions cart.js already owns, then tell the shopper what happened.

  THE PARAMETER REPLACES THE SET, IT DOES NOT APPEND. The Cart API reference documents
  { discount: 'A' }, { discount: 'A,B' } and { discount: '' } to clear — so posting only
  the newly typed code would silently DROP every code already on the cart. Every request
  below therefore sends the full intended set: existing codes read off the rendered chips,
  plus (apply) or minus (remove) the one being changed.

  THE "DID IT WORK?" PROBLEM. Shopify silently ignores a code it cannot apply — 200 OK,
  cart unchanged, no error field. The documented /cart/update.js response is "the JSON of
  the cart" (shopify.dev/docs/api/ajax/reference/cart): it carries `total_discount` but no
  discount_applications array, so success is detected twice, in this order:
    1. total_discount went UP, measured against [data-pn-cart-total-discount] — an
       attribute rendered INSIDE the region that re-renders, so it is never stale;
    2. failing that, a row bearing the submitted code appeared in the freshly swapped
       summary — server truth from Liquid's cart.discount_applications, which covers a
       zero-value code and a product-scoped one.
  Neither fires for a FREE-SHIPPING code on the drawer, where no code rows are rendered —
  shipping is not priced until checkout. pn_discount_invalid is worded to stay true in
  that case, and the code is still registered for checkout.

  NO cartUpdate PUBLISH, ON PURPOSE. cart.js:38 subscribes to PUB_SUB_EVENTS.cartUpdate
  for every source that is not 'cart-items' and answers by re-fetching the cart section
  and re-rendering it. Publishing here would repeat the swap below over a second
  round-trip, with a flash. Nothing else needs telling: a discount changes prices, not
  item count, so the cart icon bubble is already correct. (If you ever do add a publish,
  reference PUB_SUB_EVENTS bare — pubsub.js declares it as a top-level `const` in a
  classic script, so it is a script-scope binding and NOT a window property.
  assets/pn-atc.js:36 uses the window.* form and is silently dead because of it.)

  SELF-DESTRUCTION IS NORMAL. In the drawer this element sits inside .cart-drawer__footer,
  which the swap replaces — so `this` is detached mid-flight. Every post-swap DOM read
  therefore re-queries the document rather than trusting a held reference.
*/
(() => {
  if (customElements.get('pn-cart-discount')) return;

  const num = (v) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
  };
  const norm = (v) => String(v || '').trim().toUpperCase();

  const totalDiscount = () =>
    num(document.querySelector('[data-pn-cart-total-discount]')?.dataset.pnCartTotalDiscount);

  const appliedCodes = () =>
    Array.from(document.querySelectorAll('[data-pn-discount-code]'))
      .map((n) => String(n.dataset.pnDiscountCode || '').trim())
      .filter(Boolean);

  // Server truth after a swap: a chip for this code, or Dawn's discount row naming it.
  const codeIsRendered = (code) => {
    const target = norm(code);
    if (appliedCodes().some((c) => norm(c) === target)) return true;
    return Array.from(document.querySelectorAll('.discounts__discount')).some((n) =>
      norm(n.textContent).includes(target)
    );
  };

  class PnCartDiscount extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-pn-discount-form]');
      this.input = this.querySelector('input[name="discount"]');
      this.context = this.dataset.context === 'drawer' ? 'drawer' : 'page';
      this.feedbackId = this.querySelector('.pn-discount__feedback')?.id;
      if (!this.form || !this.input) return;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    // The feedback node may have been replaced by the swap, so address it by id.
    say(message, isError) {
      const out = this.feedbackId && document.getElementById(this.feedbackId);
      if (!out) return;
      out.textContent = message || '';
      out.classList.toggle('pn-discount__feedback--error', Boolean(isError));
    }

    // Which sections this surface needs back, and where each one lands. Mirrors
    // CartDrawerItems.getSectionsToRender (drawer) and CartItems.getSectionsToRender
    // (page) so the swap lands exactly where Dawn's own swaps land.
    targets() {
      if (this.context === 'drawer') {
        return [{ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' }];
      }
      return [
        ['main-cart-items', document.getElementById('main-cart-items')],
        ['main-cart-footer', document.getElementById('main-cart-footer')],
      ]
        .filter(([, host]) => host)
        .map(([id, host]) => ({ id, section: host.dataset.id, selector: '.js-contents' }));
    }

    onSubmit(event) {
      // No fetch? Let the native POST happen — the no-JS path is a real form.
      if (typeof fetch !== 'function') return;
      event.preventDefault();

      const code = (this.input.value || '').trim();
      if (!code) {
        this.say(this.dataset.msgEmpty, true);
        this.input.focus();
        return;
      }
      if (appliedCodes().some((c) => norm(c) === norm(code))) {
        this.say((this.dataset.msgApplied || '').replace('[code]', code), false);
        this.input.value = '';
        return;
      }

      const next = appliedCodes().concat(code);
      this.submit(next, (worked) =>
        worked
          ? { message: (this.dataset.msgApplied || '').replace('[code]', code), error: false }
          : { message: this.dataset.msgInvalid, error: true },
        code
      );
    }

    // NOT named remove(): that is Element.prototype.remove, and shadowing it on a
    // custom element means anything that calls el.remove() to detach this node would
    // fire a discount request instead. Dawn's HTMLUpdateUtility does exactly that.
    removeCode(code) {
      const next = appliedCodes().filter((c) => norm(c) !== norm(code));
      this.submit(next, () => ({ message: this.dataset.msgRemoved, error: false }), null);
    }

    // codes: the FULL intended set. verdict(worked) -> {message, error}.
    submit(codes, verdict, probeCode) {
      const targets = this.targets();
      const before = totalDiscount();
      this.form?.setAttribute('aria-busy', 'true');
      this.say('');

      fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          discount: codes.join(','),
          sections: targets.map((t) => t.section),
          sections_url: window.location.pathname,
        }),
      })
        .then((response) => response.json())
        .then((cart) => {
          targets.forEach((target) => {
            const host = document.getElementById(target.id);
            const html = cart.sections?.[target.section];
            if (!host || !html) return;
            const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
            const slot = host.querySelector(target.selector) || host;
            if (parsed) slot.innerHTML = parsed.innerHTML;
          });

          const worked = num(cart.total_discount) > before || (probeCode ? codeIsRendered(probeCode) : true);
          const said = verdict(worked);
          this.say(said.message, said.error);
          if (worked && this.input) this.input.value = '';
        })
        .catch(() => {
          this.say(this.dataset.msgError, true);
        })
        .finally(() => {
          document
            .querySelector(`pn-cart-discount[data-context="${this.context}"] [data-pn-discount-form]`)
            ?.removeAttribute('aria-busy');
        });
    }
  }

  customElements.define('pn-cart-discount', PnCartDiscount);

  // Remove buttons live inside .js-contents, which is destroyed and rebuilt on every
  // cart update — so the listener is delegated from the document and bound once.
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-pn-discount-remove]');
    if (!button) return;
    event.preventDefault();
    const host =
      button.closest('#main-cart-footer')?.querySelector('pn-cart-discount') ||
      document.querySelector('pn-cart-discount[data-context="page"]');
    host?.removeCode?.(button.dataset.pnDiscountRemove);
  });
})();
